<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ContractEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    contract: {
      type: Object,
      required: false,
    },
    target: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const getPayload = (...args) => {
    const [data] = args;

    return props.target
      ? _.pick(data, targetConfig.value[props.target].fieldsToSubmit)
      : data;
  };

  const formId = ref(null);
  const defaultTitle = ref(
    $fn.value.pgettext('Contract', 'Edit General Information')
  );
  const targetConfig = ref({
    company: {
      title: $fn.value.pgettext('Contract', 'Edit company'),
      fieldsToSubmit: ['id', 'revision_id', 'etag', 'company'],
    },
    counterparty: {
      title: $fn.value.pgettext('Contract', 'Edit counterparty'),
      fieldsToSubmit: ['id', 'revision_id', 'etag', 'counterparty'],
    },
    invoicing: {
      title: $fn.value.pgettext('Contract', 'Edit invoicing information'),
      fieldsToSubmit: ['id', 'revision_id', 'etag', 'invoicing'],
    },
    lifecycle: {
      title: $fn.value.pgettext('Contract', 'Edit lifecycle information'),
      fieldsToSubmit: ['id', 'revision_id', 'etag', 'lifecycle'],
    },
    rental: {
      title: $fn.value.pgettext('Contract', 'Edit rental'),
      fieldsToSubmit: ['id', 'revision_id', 'etag', 'rental'],
    },
    general: {
      title: $fn.value.pgettext('Contract', 'Edit general data'),
      fieldsToSubmit: [
        'id',
        'revision_id',
        'etag',
        'extras',
        'revision_number',
        'status',
        'contract_type',
        'contract_number',
        'external_key',
        'category_id',
      ],
    },
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'md',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    defaultTitle: defaultTitle.value,
    targetConfig: targetConfig.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'defaultTitle' },
      { name: 'targetConfig' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId },
    ux: {},
    dataRefs: {
      formId,
      defaultTitle,
      targetConfig,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: props.target
          ? targetConfig.value[props.target].title
          : defaultTitle.value,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.target,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('target');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_mg22ewhbt_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_855tyskqe_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_crud_7m0f3h28x_getActionArgs = async ($event) => {
    return {
      objectType: 'contract',
      op: 'upsert',
      data: getPayload(props.contract),
    };
  };

  const act_7315092382398562_closeModal_mg22ewhbt =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_mg22ewhbt_getActionArgs,
    });
  const inst_evh_7315092382398562_close_dcjr4990k =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_mg22ewhbt,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_dcjr4990k],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_s5jdqg7lh =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_9ae1hn7wi =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_s5jdqg7lh,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_9ae1hn7wi],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_t9i48lid0 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_lyz2t1rtf =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_t9i48lid0,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_lyz2t1rtf],
    event: 'close',
  });
  const act_1040167445267876_closeModal_855tyskqe =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_855tyskqe_getActionArgs,
    });
  const inst_evh_1040167445267876_success_7ygtl2082 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_855tyskqe,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_crud_7m0f3h28x = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_7m0f3h28x_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_7ygtl2082],
  });
  const inst_evh_1040167445267876_submit_73b3w202t =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_7m0f3h28x,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_73b3w202t],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_tny4fopry =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_0pd9vvvp7 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_tny4fopry,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_0pd9vvvp7],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_dcjr4990k,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_9ae1hn7wi,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_lyz2t1rtf,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_7ygtl2082,
    inst_evh_1040167445267876_submit_73b3w202t,
    save: inst_evh_1040167445267876_submit_73b3w202t,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_0pd9vvvp7,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_dcjr4990k = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_dcjr4990k.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_9ae1hn7wi = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_9ae1hn7wi.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_lyz2t1rtf = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_lyz2t1rtf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_7ygtl2082 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_7ygtl2082.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_73b3w202t = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_73b3w202t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_0pd9vvvp7 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_0pd9vvvp7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ContractEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="contract"
        class="row"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <ContractEditorFieldset
            :contract="contract"
            :formId="formId"
            :target="target"
          >
          </ContractEditorFieldset>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms.contract || !forms.contract.submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
